/**
 * ImpersonationsPanel — Sous-onglet « Impersonations » de
 * Paramètres > Historique de connexion (spec iter194, 2026-02-28).
 *
 * Réservé au Premier Admin. Liste toutes les sessions
 * « Se connecter en tant que » qu'il a initiées, avec :
 *   • Cible (nom + email)
 *   • Date de début
 *   • Durée (en cours / xxx s / xxx min)
 *   • Statut (En cours / Fermée)
 */

import HistoryPanel from "@/components/common/HistoryPanel";
import { useEffect, useState, useMemo } from "react";
import HistoryTimeline, { HISTORY_COLORS } from "@/components/ui/HistoryTimeline";
import { toast } from "@/lib/toast";
import { Eye, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDuration(seconds) {
  if (seconds == null) return null;
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s ? `${m} min ${s} s` : `${m} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Calcule la durée « live » pour les sessions non-closes. */
function liveDuration(startedIso, nowMs) {
  try {
    const start = new Date(startedIso).getTime();
    return Math.max(0, Math.floor((nowMs - start) / 1000));
  } catch { return null; }
}

export default function ImpersonationsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/login-history/impersonations", { params: { limit: 300 } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error("Impossible de charger les impersonations", {
        description: err.response?.data?.detail,
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Tick 1s pour rafraîchir la durée « en cours » des sessions ouvertes.
  useEffect(() => {
    const hasOpen = rows.some(r => !r.ended_at);
    if (!hasOpen) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [rows]);

  const stats = useMemo(() => {
    const open = rows.filter(r => !r.ended_at).length;
    return { total: rows.length, open };
  }, [rows]);

  return (
    <div className="space-y-5" data-testid="impersonations-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-muted-foreground">
          <span className="tabular-nums font-semibold text-foreground">{stats.total}</span> session
          {stats.total !== 1 ? "s" : ""} d&apos;impersonation
          {stats.open > 0 && (
            <span className="ml-2 inline-flex items-center gap-1">
              · <span className="tabular-nums font-semibold" style={{ color: "#F5C842" }}>{stats.open}</span>
              <span style={{ color: "#F5C842" }}>en cours</span>
            </span>
          )}
        </div>
        <Button
          variant="outline"
          className="border-vertec-borderDark gap-2"
          onClick={load}
          disabled={loading}
          data-testid="btn-refresh-impersonations"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm py-10 text-center">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground text-sm py-10 text-center border border-dashed border-vertec-borderDark rounded-md" data-testid="impersonations-empty">
          Aucune session d&apos;impersonation à afficher.
          <div className="mt-1 text-xs opacity-70">
            Utilisez l&apos;icône œil dans Paramètres → Utilisateurs pour en initier une.
          </div>
        </div>
      ) : (
        <div className="rounded-md border border-vertec-borderDark overflow-hidden">
          <HistoryPanel entityType="impersonation" entityId="all" label="Sessions ouvertes par le Premier Admin" entityLabel="les sessions" height={560} testId="impersonations-timeline" />
        </div>
      )}
    </div>
  );
}
